import { ANNUAL_PER_LOCATION_PRICE_USD, PER_LOCATION_PRICE_USD } from "@/lib/pricing";

/**
 * Thin wrappers around GTM dataLayer tracking.
 *
 * Components can call these without guarding; analytics must never break the
 * product when scripts are blocked, consent is declined, or SSR is rendering.
 */

const DEFAULT_PURCHASE_ITEM_ID = "onetapreply_pro_location";
const DEFAULT_PURCHASE_ITEM_NAME = "OneTapReply Pro location subscription";

export const GTM_SOURCE_EVENTS = {
  BEGIN_CHECKOUT: "otr_begin_checkout",
  PURCHASE: "otr_purchase",
  SIGN_UP: "otr_sign_up",
  ONBOARDING_BOOTSTRAP_COMPLETED: "otr_onboarding_bootstrap_completed",
} as const;

export interface PurchaseTrackingPayload {
  transactionId: string;
  value: number;
  currency: string;
  businessId?: string | null;
  surface?: string | null;
  itemId?: string | null;
  itemName?: string | null;
}

export interface CheckoutTrackingPayload {
  value?: number | null;
  currency?: string | null;
  businessId?: string | null;
  surface?: string | null;
  funnel?: string | null;
  itemId?: string | null;
  itemName?: string | null;
}

function getDataLayer(): unknown[] | null {
  if (typeof window === "undefined") return null;
  if (!Array.isArray(window.dataLayer)) window.dataLayer = [];
  return window.dataLayer;
}

function purchaseStorageKey(transactionId: string): string {
  return `otr_purchase_tracked:${transactionId}`;
}

function hasTrackedPurchase(transactionId: string): boolean {
  if (typeof window === "undefined") return false;
  const key = purchaseStorageKey(transactionId);
  try {
    return (
      window.localStorage.getItem(key) === "1" ||
      window.sessionStorage.getItem(key) === "1"
    );
  } catch {
    return false;
  }
}

function canPersistPurchaseDedupe(): boolean {
  if (typeof window === "undefined") return false;
  return window.__otrMarketingTagsLoaded?.gtm === true;
}

function rememberTrackedPurchase(transactionId: string, { persist }: { persist: boolean }): void {
  if (typeof window === "undefined") return;
  const key = purchaseStorageKey(transactionId);
  try {
    window.sessionStorage.setItem(key, "1");
    if (persist) {
      window.localStorage.setItem(key, "1");
    }
  } catch {
    // Storage may be unavailable. GA4 still dedupes purchase by transaction_id.
  }
}

function stripUndefined(params: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== undefined && value !== null),
  );
}

export function track(event: string, props: Record<string, unknown> = {}): boolean {
  const dataLayer = getDataLayer();
  if (!dataLayer) return false;
  try {
    dataLayer.push({ event, ...props });
    return true;
  } catch {
    return false;
  }
}

export function checkoutValueForInterval(billingInterval: "monthly" | "annual"): number {
  return billingInterval === "annual" ? ANNUAL_PER_LOCATION_PRICE_USD : PER_LOCATION_PRICE_USD;
}

export function trackGtagEvent(
  event: string,
  params: Record<string, unknown> = {},
): boolean {
  return track(event, params);
}

export function trackPurchase(payload: PurchaseTrackingPayload): boolean {
  const currency = payload.currency.trim().toUpperCase();
  const value = Number(payload.value);
  if (!payload.transactionId || !currency || !Number.isFinite(value)) return false;

  return trackGtagEvent(GTM_SOURCE_EVENTS.PURCHASE, {
    event_name: "purchase",
    transaction_id: payload.transactionId,
    value,
    currency,
    business_id: payload.businessId ?? undefined,
    surface: payload.surface ?? undefined,
    items: [
      {
        item_id: payload.itemId || DEFAULT_PURCHASE_ITEM_ID,
        item_name: payload.itemName || DEFAULT_PURCHASE_ITEM_NAME,
        item_category: "subscription",
        price: value,
        quantity: 1,
      },
    ],
  });
}

export function trackPurchaseOnce(payload: PurchaseTrackingPayload): boolean {
  if (hasTrackedPurchase(payload.transactionId)) return false;
  const tracked = trackPurchase(payload);
  if (tracked) {
    rememberTrackedPurchase(payload.transactionId, { persist: canPersistPurchaseDedupe() });
  }
  return tracked;
}

export function trackBeginCheckout(payload: CheckoutTrackingPayload = {}): boolean {
  const currency = (payload.currency || "USD").trim().toUpperCase();
  const value = payload.value === null || payload.value === undefined
    ? undefined
    : Number(payload.value);
  const params = stripUndefined({
    value: Number.isFinite(value) ? value : undefined,
    currency,
    business_id: payload.businessId,
    surface: payload.surface,
    funnel: payload.funnel,
    items: [
      {
        item_id: payload.itemId || DEFAULT_PURCHASE_ITEM_ID,
        item_name: payload.itemName || DEFAULT_PURCHASE_ITEM_NAME,
        item_category: "subscription",
        price: Number.isFinite(value) ? value : undefined,
        quantity: 1,
      },
    ],
  });

  return trackGtagEvent(GTM_SOURCE_EVENTS.BEGIN_CHECKOUT, {
    event_name: "begin_checkout",
    ...params,
  });
}

// Discriminated union of homepage events for type-safe call sites.
export const HOME_EVENTS = {
  HERO_CTA_CLICK: "home_hero_cta_click",
  PRICING_CHANGE: "home_pricing_change",
  PRICING_CTA_CLICK: "home_pricing_cta_click",
  FAQ_OPEN: "home_faq_open",
  SMS_PHONE_REPLAY: "home_sms_phone_replay",
  SECTION_IN_VIEW: "home_section_in_view",
} as const;

export const LANDING_EVENTS = {
  PRIMARY_CTA_CLICK: "landing_primary_cta_click",
  HEALTH_CHECK_CTA_CLICK: "landing_health_check_cta_click",
  PRICING_CLICK: "landing_pricing_click",
} as const;
